import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { Input } from "@/shared/ui/components/ui/input";
import { Label } from "@/shared/ui/components/ui/label";
import { Select, type SelectOption } from "./Select";
import { cn } from "@/shared/lib/utils";

export interface NumberSelectInputBaseProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectOptions: SelectOption[];
  selectValue?: string;
  onSelectChange?: (value: string) => void;
  selectPlaceholder?: string;
  onErrorClear?: () => void;
  selectClassName?: string;
}

export interface NumberSelectInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TNumberName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TSelectName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<
  NumberSelectInputBaseProps,
  "name" | "value" | "onChange" | "selectValue" | "onSelectChange" | "error"
> {
  numberName?: TNumberName;
  selectName?: TSelectName;
  control?: Control<TFieldValues>;
  numberRules?: Omit<
    RegisterOptions<TFieldValues, TNumberName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  selectRules?: Omit<
    RegisterOptions<TFieldValues, TSelectName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectValue?: string;
  onSelectChange?: (value: string) => void;
  error?: string;
}

const NumberSelectInputBase = React.forwardRef<
  HTMLInputElement,
  NumberSelectInputBaseProps
>(
  (
    {
      className,
      selectClassName,
      label,
      hint,
      error,
      leftIcon,
      value,
      onChange,
      disabled,
      id,
      selectOptions,
      selectValue,
      onSelectChange,
      selectPlaceholder,
      onErrorClear,
      ...props
    },
    ref,
  ) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hasError && onErrorClear) {
        onErrorClear();
      }
      onChange?.(e);
    };

    const handleSelectChange = (value: string) => {
      if (hasError && onErrorClear) {
        onErrorClear();
      }
      onSelectChange?.(value);
    };

    return (
      <div className={cn("w-full gap-3", className)}>
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "mb-3 block text-sm font-medium text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700",
            )}
          >
            {label}
          </Label>
        )}

        <div
          className={cn(
            "flex rounded-lg border overflow-hidden",
            hasError ? "border-error-700" : "border-grey-200",
            disabled && "border-grey-100 bg-grey-50",
          )}
        >
          <div
            className={cn(
              "relative flex-shrink-0 border-e",
              hasError ? "border-error-700" : "border-grey-200",
            )}
          >
            <Select
              options={selectOptions}
              placeholder={selectPlaceholder}
              value={selectValue || ""}
              onChange={handleSelectChange}
              disabled={disabled}
              error={hasError ? " " : undefined}
              hint=""
              className={cn(
                "h-full w-full min-w-[130px] rounded-none border-0 bg-grey-50 px-3 py-4 pe-8 text-sm font-normal",
                "focus:outline-none focus:ring-0",
                "disabled:cursor-not-allowed disabled:border-grey-100 disabled:bg-grey-50",
                hasError &&
                  "bg-error-bg border-error-700 text-error-700 focus:bg-error-bg",
                !hasError &&
                  "text-grey-900 hover:bg-grey-100 focus:bg-grey-100",
                selectClassName,
              )}
            />
          </div>

          <div className="relative flex-1 group">
            {leftIcon && (
              <div
                className={cn(
                  "absolute start-5 top-1/2 -translate-y-1/2 transition-colors text-grey-200 z-10",
                  "group-focus-within:text-grey-900",
                  disabled && "text-grey-200",
                  hasError &&
                    "text-error-700 group-focus-within:text-error-700",
                )}
              >
                {leftIcon}
              </div>
            )}

            <Input
              id={inputId}
              ref={ref}
              type="number"
              value={value ?? ""}
              onChange={handleChange}
              disabled={disabled}
              className={cn(
                "px-5 py-4 bg-white text-sm font-normal shadow-none",
                "focus-visible:ring-0",
                "disabled:bg-grey-50 border-grey-100",
                "placeholder:text-grey-200",
                leftIcon && "ps-12",
                "rounded-none rounded-e-lg border-0",
                "hover:border-brand-green hover:text-grey-200",
                "focus-visible:border-brand-green focus-visible:text-grey-900",
                // Hide number input arrows
                "[appearance:textfield]",
                "[&::-webkit-outer-spin-button]:appearance-none",
                "[&::-webkit-inner-spin-button]:appearance-none",
                disabled &&
                  "bg-grey-50 border-grey-100 text-grey-200 cursor-not-allowed hover:text-grey-200 hover:border-grey-100",
                hasError &&
                  "bg-error-bg text-error-700 placeholder:text-error-700 hover:text-error-700 focus-visible:text-error-700",
                className,
              )}
              {...props}
            />
          </div>
        </div>

        {(hint || error) && (
          <p
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              hasError && "text-error-700",
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  },
);

NumberSelectInputBase.displayName = "NumberSelectInputBase";

const NumberSelectInput = React.forwardRef<
  HTMLInputElement,
  NumberSelectInputProps<
    FieldValues,
    FieldPath<FieldValues>,
    FieldPath<FieldValues>
  >
>((props, ref) => {
  const {
    numberName,
    selectName,
    control,
    numberRules,
    selectRules,
    error: errorProp,
    value: valueProp,
    onChange: onChangeProp,
    selectValue: selectValueProp,
    onSelectChange: onSelectChangeProp,
    ...restProps
  } = props;

  if (control && numberName && selectName) {
    return (
      <Controller
        name={numberName as any}
        control={control as any}
        rules={numberRules as any}
        render={({ field: numberField, fieldState: numberFieldState }) => (
          <Controller
            name={selectName as any}
            control={control as any}
            rules={selectRules as any}
            render={({ field: selectField, fieldState: selectFieldState }) => (
              <NumberSelectInputBase
                {...restProps}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "." ||
                    e.key === "," ||
                    e.key === "+" ||
                    e.key === "-"
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  if (
                    e.clipboardData.getData("text").includes("e") ||
                    e.clipboardData.getData("text").includes("E") ||
                    e.clipboardData.getData("text").includes(".") ||
                    e.clipboardData.getData("text").includes(",") ||
                    e.clipboardData.getData("text").includes("+") ||
                    e.clipboardData.getData("text").includes("-")
                  ) {
                    e.preventDefault();
                  }
                }}
                value={numberField.value ?? ""}
                onChange={(e) => numberField.onChange(e.target.value)}
                selectValue={selectField.value ?? ""}
                onSelectChange={selectField.onChange}
                error={
                  numberFieldState.error?.message ||
                  selectFieldState.error?.message ||
                  errorProp
                }
                onErrorClear={() => {}}
                ref={ref}
              />
            )}
          />
        )}
      />
    );
  }

  if (control && numberName) {
    return (
      <Controller
        name={numberName as any}
        control={control as any}
        rules={numberRules as any}
        render={({ field: numberField, fieldState: numberFieldState }) => (
          <NumberSelectInputBase
            {...restProps}
            value={numberField.value ?? ""}
            onChange={(e) => numberField.onChange(e.target.value)}
            selectValue={selectValueProp}
            onSelectChange={onSelectChangeProp}
            error={numberFieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  if (control && selectName) {
    return (
      <Controller
        name={selectName as any}
        control={control as any}
        rules={selectRules as any}
        render={({ field: selectField, fieldState: selectFieldState }) => (
          <NumberSelectInputBase
            {...restProps}
            value={valueProp}
            onChange={onChangeProp}
            selectValue={selectField.value ?? ""}
            onSelectChange={selectField.onChange}
            error={selectFieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  return (
    <NumberSelectInputBase
      {...restProps}
      value={valueProp}
      onChange={onChangeProp}
      selectValue={selectValueProp}
      onSelectChange={onSelectChangeProp}
      error={errorProp}
      ref={ref}
    />
  );
});

NumberSelectInput.displayName = "NumberSelectInput";

export { NumberSelectInput };
